import { BidderRule } from '@pubstack/common/src/bidder-rule'
import { AdUnit } from '@pubstack/common/src/adunit'
import { SiteSpaConfig } from '@pubstack/common/src/kleanadsScopeConfig'
import { PbsAdUnit } from '@pubstack/common/src/pbs/adunit'
import { Site, SITE_AD_UNIT_ENV_COMPATIBILITY } from '@pubstack/common/src/site'
import { AdmSiteConfig } from '@pubstack/common/src/siteConfig'
import { StackContext } from '@pubstack/common/src/stackContext'
import { AdmStack, isAdmStack, isPbsStack, PbsStack } from '@pubstack/common/src/stack'
import { useAdUnits as useAdmAdUnits } from '~/api/adm/ad-units.api'
import { useContexts } from '~/api/adm/contexts.api'
import { useSiteConfig, useSpaConfig } from '~/api/adm/site-configs'
import { useSite } from '~/api/app/sites.api'
import { useAdUnits as usePbsAdUnits } from '~/api/pbs/ad-units.api'
import { useBidderRules } from '~/api/bidder-rules.api'
import { useStacks } from '~/api/stacks.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { Product } from '~/hooks/useProduct'

type CommonSitePageData = {
  scopeId: string
  site: Site
  isLoading: boolean
  product: Product
}

type PbsSitePageData = CommonSitePageData & {
  product: 'pbs'
  adUnits: PbsAdUnit[]
  bidderRules: BidderRule[]
  stacks: PbsStack[]
}

type AdmSitePageData = CommonSitePageData & {
  product: 'adm'
  adUnits: AdUnit[]
  bidderRules: BidderRule[]
  contexts: StackContext[]
  siteConfig?: AdmSiteConfig
  spaConfig?: SiteSpaConfig
  stacks: AdmStack[]
}

export type PbsSitePageContext = PbsSitePageData & {
  site: Site
}

export type AdmSitePageContext = AdmSitePageData & {
  site: Site
}

export const isAdmContext = (props: PbsSitePageContext | AdmSitePageContext): props is AdmSitePageContext => props.product === 'adm'

const EMPTY_SITE: Site = {
  id: '',
  name: '',
  adManagementEnabled: false,
  env: 'web',
  admStatus: 'inactive',
  enabled: false,
  pbsStandaloneStatus: 'inactive',
}

export const usePbsSitePageContext = (siteId?: string): PbsSitePageData => {
  // API Hooks
  const user = useAuthenticatedUser()
  const scopeId = user.currentScopeId
  const { data: site, isLoading: isSiteLoading } = useSite(siteId || '', scopeId)
  const { data: stacks, isLoading: isStacksLoading } = useStacks({ siteId, status: ['to-deploy', 'live'], type: 'pbs' })
  // Gated on the media, not on its `env`: a media created before that field existed carries none, and
  // indexing the table with `undefined` used to leave it with an empty ad unit list — while it reads as
  // web everywhere else it is shown. Node's inventory on purpose, even where One runs: what reads this is
  // the Prebid Server connect and activation screens, which count and list the ad units the customer
  // deploys server-side. The stack screens ask `useStackInventory` instead.
  const adUnitsEnvs = site ? (SITE_AD_UNIT_ENV_COMPATIBILITY[site.env] ?? SITE_AD_UNIT_ENV_COMPATIBILITY.web) : undefined
  const { data: adUnits, isLoading: isAdUnitsLoading } = usePbsAdUnits({ enabled: true, env: adUnitsEnvs }, !!site)
  const { data: bidderRules, isLoading: isBidderRulesLoading } = useBidderRules('pbs')

  return {
    product: 'pbs',
    scopeId,
    site: site || EMPTY_SITE,
    stacks: stacks.filter(isPbsStack),
    adUnits,
    bidderRules,
    isLoading: isSiteLoading || isStacksLoading || isAdUnitsLoading || isBidderRulesLoading,
  }
}

export const useAdmSitePageContext = (siteId?: string): AdmSitePageData => {
  // API Hooks
  const user = useAuthenticatedUser()
  const scopeId = user.currentScopeId
  const { data: site, isLoading: isSiteLoading } = useSite(siteId || '', scopeId)
  const { data: stacks, isLoading: isStacksLoading } = useStacks({ siteId, status: ['to-deploy', 'live', 'archived_group'], type: 'adm' })
  const { data: adUnits, isLoading: isAdUnitsLoading } = useAdmAdUnits()
  const { data: bidderRules, isLoading: isBidderRulesLoading } = useBidderRules('adm')
  const { data: contexts, isLoading: isContextsLoading } = useContexts()
  const { data: siteConfig, isLoading: isSiteConfigLoading } = useSiteConfig(siteId)
  const { data: spaConfig, isLoading: isSpaConfigLoading } = useSpaConfig(siteId)

  return {
    product: 'adm',
    scopeId,
    site: site || EMPTY_SITE,
    stacks: stacks.filter(isAdmStack),
    adUnits,
    bidderRules,
    contexts,
    siteConfig,
    spaConfig,
    isLoading: isSiteLoading || isStacksLoading || isAdUnitsLoading || isBidderRulesLoading || isContextsLoading || isSiteConfigLoading || isSpaConfigLoading,
  }
}
